/**
 * LUKHAS.AI Authentication Context
 *
 * Provides authentication state and methods to React components.
 * Integrates with lukhas.id SSO for authentication.
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import {
  SSOUser,
  AccessState,
  getSession,
  redirectToLogin,
  logout as ssoLogout,
  deriveAccessState,
} from './ssoClient'

// DEV BYPASS: Only active when VITE_DEV_BYPASS_AUTH=true is explicitly set
const DEV_BYPASS_AUTH = import.meta.env.DEV && import.meta.env.VITE_DEV_BYPASS_AUTH === 'true'

// Mock user for development
const DEV_MOCK_USER: SSOUser = {
  id: 'dev-user-001',
  email: 'dev@lukhas.ai',
  lambdaId: 'ΛiD#7842',
  displayName: 'Dev User',
  avatar: undefined,
  roles: ['developer', 'admin'],
  tier: 'pro',
  organization: 'LUKHAS AI',
  createdAt: '2024-01-15T00:00:00Z',
}

interface AuthContextValue {
  // State
  user: SSOUser | null
  isAuthenticated: boolean
  isLoading: boolean
  accessState: AccessState

  // Actions
  login: (returnPath?: string) => void
  logout: () => Promise<void>
  refreshAuth: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

/**
 * Auth Provider - wraps app to provide authentication context
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<SSOUser | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // Initialize auth state from session
  const refreshAuth = useCallback(() => {
    setIsLoading(true)
    try {
      // DEV BYPASS: Use mock user in development
      if (DEV_BYPASS_AUTH) {
        setUser(DEV_MOCK_USER)
        setIsAuthenticated(true)
        return
      }

      const session = getSession()
      if (session) {
        setUser(session.user)
        setIsAuthenticated(true)
      } else {
        setUser(null)
        setIsAuthenticated(false)
      }
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Check session on mount
  useEffect(() => {
    refreshAuth()
  }, [refreshAuth])

  // Cross-tab logout via BroadcastChannel (sessionStorage doesn't fire storage events)
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return
    const channel = new BroadcastChannel('lukhas_auth')
    channel.onmessage = (e) => {
      if (e.data === 'logout') {
        setUser(null)
        setIsAuthenticated(false)
      }
    }
    return () => channel.close()
  }, [])

  // Login - redirect to lukhas.id (no-op in dev bypass mode)
  const login = useCallback((returnPath?: string) => {
    if (DEV_BYPASS_AUTH) {
      console.log('[DEV] Auth bypass active - already logged in as mock user')
      return
    }
    redirectToLogin(returnPath)
  }, [])

  // Logout - clear session, broadcast to other tabs, redirect
  const logout = useCallback(async () => {
    if (DEV_BYPASS_AUTH) {
      console.log('[DEV] Auth bypass active - logout simulated (refresh to restore)')
      setUser(null)
      setIsAuthenticated(false)
      return
    }
    setIsLoading(true)
    try {
      await ssoLogout()
      // Notify other tabs
      if (typeof BroadcastChannel !== 'undefined') {
        const channel = new BroadcastChannel('lukhas_auth')
        channel.postMessage('logout')
        channel.close()
      }
    } finally {
      setUser(null)
      setIsAuthenticated(false)
      setIsLoading(false)
    }
  }, [])

  const accessState = deriveAccessState(user)

  const value: AuthContextValue = {
    user,
    isAuthenticated,
    isLoading,
    accessState,
    login,
    logout,
    refreshAuth,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

/**
 * Hook to access auth context
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

/**
 * Hook to require authentication
 * Redirects to login if not authenticated
 */
export function useRequireAuth(returnPath?: string): AuthContextValue {
  const auth = useAuth()

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated) {
      auth.login(returnPath || window.location.pathname)
    }
  }, [auth, returnPath])

  return auth
}
